import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { clearCart } = useCart();

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      navigate('/');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentStatus(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(attempts + 1);
          checkPaymentStatus();
        }, pollInterval);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="bg-off_white min-h-screen flex items-center justify-center py-12" data-testid="order-confirmation-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border-4 border-black rounded-2xl p-12 shadow-hard-lg text-center">
          {status === 'checking' && (
            <div data-testid="status-checking">
              <div className="bg-brand_yellow w-24 h-24 rounded-full border-4 border-black mx-auto mb-6 flex items-center justify-center">
                <Loader2 className="w-12 h-12 animate-spin" strokeWidth={3} />
              </div>
              <h1 className="font-chewy text-4xl text-brand_blue mb-4">Processing Payment...</h1>
              <p className="font-nunito text-lg text-gray-700 font-semibold">
                Please wait while we confirm your payment.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="status-success">
              <div className="bg-brand_green w-24 h-24 rounded-full border-4 border-black mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-white" strokeWidth={3} />
              </div>
              <h1 className="font-chewy text-4xl text-brand_blue mb-4">Order Confirmed!</h1>
              <p className="font-nunito text-lg text-gray-700 mb-8 font-semibold">
                Thank you for your order! We'll send you a confirmation email shortly.
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => navigate('/track-order')}
                  className="w-full bg-brand_blue text-white font-chewy text-xl px-8 py-4 rounded-full border-4 border-black shadow-hard-lg hover:shadow-hard-hover hover:-translate-y-1 transition-all"
                  data-testid="track-order-button"
                >
                  TRACK YOUR ORDER
                </button>
                <button
                  onClick={() => navigate('/products')}
                  className="w-full bg-white text-black font-nunito font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
                  data-testid="continue-shopping-button"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          )}

          {(status === 'error' || status === 'timeout') && (
            <div data-testid="status-error">
              <div className="bg-brand_red w-24 h-24 rounded-full border-4 border-black mx-auto mb-6 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-white" strokeWidth={3} />
              </div>
              <h1 className="font-chewy text-4xl text-brand_red mb-4">Payment Issue</h1>
              <p className="font-nunito text-lg text-gray-700 mb-8 font-semibold">
                {status === 'timeout'
                  ? 'Payment verification timed out. Please check your email for confirmation or contact us.'
                  : 'There was an issue with your payment. Please try again or contact us for assistance.'}
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-brand_blue text-white font-chewy text-xl px-8 py-4 rounded-full border-4 border-black shadow-hard-lg hover:shadow-hard-hover hover:-translate-y-1 transition-all"
                  data-testid="retry-button"
                >
                  TRY AGAIN
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-white text-black font-nunito font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
                  data-testid="home-button"
                >
                  Go Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;